import { useEffect, useRef } from 'react'

/**
 * A screen that reloads itself after a deploy.
 *
 * A kiosk or hall board loads the app once and runs it for weeks. Its data stays
 * live over the socket, so a new design reaches it at once; the code that draws
 * the design does not. Found on 1 October 2026: a kiosk started before a fix to
 * the ticket number's alignment kept drawing it right-aligned after the fix was
 * deployed, while the editor showed it centred.
 *
 * So the screen knows which build it was made from (stamped by ci/pipeline.sh;
 * in development, the server's first answer), asks the server which build it
 * runs, and reloads once the two differ — but only while nobody is using it, so a
 * customer halfway through choosing a service never has the screen vanish.
 */

/** The commit this bundle was built from, or '' when it was not stamped (development). */
const STAMPED = typeof __QMS_BUILD__ === 'string' ? __QMS_BUILD__ : ''

/** The first build the server named, for an unstamped bundle. */
let firstHeard: string | undefined

/** Which build this page is running, as far as it knows: the stamp, or failing
 *  that the server's answer when the page first asked. */
export function appBuild(): string | undefined {
  return STAMPED || firstHeard
}

/** How often a screen asks which build the server runs. */
export const BUILD_CHECK_MS = 5 * 60_000

/** Whether a screen that started on `first` and now hears `current` should reload. */
export function shouldReload(first: string | undefined, current: string | undefined, idle: boolean): boolean {
  return idle && !!first && !!current && first !== current
}

/** The build the server runs, from /healthz. */
export async function serverBuild(): Promise<string | undefined> {
  try {
    const res = await fetch('/healthz', { cache: 'no-store' })
    if (!res.ok) return undefined
    const body = (await res.json()) as { commit?: string }
    return body.commit || undefined
  } catch {
    return undefined
  }
}

export function useReloadOnNewBuild(idle: boolean) {
  const first = useRef<string | undefined>(STAMPED || undefined)
  const pending = useRef<string | undefined>(undefined)
  const idleNow = useRef(idle)
  idleNow.current = idle

  useEffect(() => {
    let stopped = false
    const check = async () => {
      const current = await serverBuild()
      if (stopped || !current) return
      if (!first.current) first.current = current
      firstHeard ??= current
      pending.current = current
      if (shouldReload(first.current, current, idleNow.current)) window.location.reload()
    }
    void check()
    const timer = window.setInterval(check, BUILD_CHECK_MS)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [])

  // A new build heard while busy is taken the moment the screen is idle again.
  useEffect(() => {
    if (shouldReload(first.current, pending.current, idle)) window.location.reload()
  }, [idle])
}

/** Whether the server has a newer build than this staff page runs. */
let behind = false

export function staleBuild(): boolean {
  return behind
}

/** Asks the server once which build it runs; staleBuild answers from the latest reply. */
export async function checkBuild(): Promise<void> {
  const current = await serverBuild()
  if (!current) return
  firstHeard ??= current
  behind = shouldReload(appBuild(), current, true)
}

/** How often an open staff page asks, besides on focus and after each move between pages. */
export const STAFF_CHECK_MS = 60_000

/** Keeps staleBuild current while a staff page is open; returns the stop. */
export function watchBuild(): () => void {
  const check = () => void checkBuild()
  const onVisible = () => {
    if (document.visibilityState === 'visible') check()
  }
  check()
  const timer = window.setInterval(check, STAFF_CHECK_MS)
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', onVisible)
  return () => {
    window.clearInterval(timer)
    window.removeEventListener('focus', check)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
